import { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/Layout';
import { Cliente, Documento, Incidencia } from '@/types';
import { getClientes, getDocumentos, getIncidencias } from '@/lib/supabaseService';
import DataTable from '@/components/DataTable';
import FilterBar, { Filter } from '@/components/FilterBar';
import { useAuth } from '@/lib/authContext';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useRouter } from 'next/router';
import { supabase } from '@/lib/supabase';

interface ClienteDetailed extends Cliente {
  docsPendientes: number;
  docsIAProcesados: number;
  incidenciasAbiertas: number;
  resultadoActual: number;
  margenActual: number;
  ultimaActividad: Date;
  estadoVisual: 'green' | 'amber' | 'red';
  asientoERPUltimo?: string;
}

interface AIContext {
  resumenGeneral: string;
  alertasActivas: string[];
  recomendaciones: string[];
  tendencias: {
    label: string;
    value: string;
    tendencia: 'up' | 'down' | 'stable';
  }[];
}

export default function AsesorClientesDetailed() {
  const { user } = useAuth();
  const router = useRouter();
  const [clientes, setClientes] = useState<ClienteDetailed[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState<Record<string, any>>({});
  const [clienteSeleccionado, setClienteSeleccionado] = useState<ClienteDetailed | null>(null);
  const [mostrarDetalle, setMostrarDetalle] = useState(false);
  const [showAIContext, setShowAIContext] = useState(false);
  const [aiContext, setAIContext] = useState<AIContext | null>(null);
  const [loadingAI, setLoadingAI] = useState(false);

  const loadClientesData = useCallback(async () => {
    if (!user?.id) return;
    try {
      const [rawClientes, allDocs, allIncs] = await Promise.all([
        getClientes(user.id),
        getDocumentos(undefined, user.id),
        getIncidencias(undefined, user.id),
      ]);

      const enrichedClientes: ClienteDetailed[] = rawClientes.map(cliente => {
        const docs = allDocs.filter(d => d.clienteId === cliente.id);
        const pendientes = docs.filter(d => d.estado === 'pendiente' || d.estado === 'procesado_ia').length;
        const iaProcesados = docs.filter(d => d.procesadoIA).length;
        const incidencias = allIncs.filter(i => i.clienteId === cliente.id && (i.estado === 'nueva' || i.estado === 'en_revision')).length;

        let estadoVisual: 'green' | 'amber' | 'red' = 'green';
        if (pendientes > 10 || incidencias > 2) estadoVisual = 'red';
        else if (pendientes > 5 || incidencias > 0) estadoVisual = 'amber';

        return {
          ...cliente,
          docsPendientes: pendientes,
          docsIAProcesados: iaProcesados,
          incidenciasAbiertas: incidencias,
          resultadoActual: 0,
          margenActual: 0,
          ultimaActividad: new Date(),
          estadoVisual,
          asientoERPUltimo: docs.find(d => d.nroAsientoERP)?.nroAsientoERP,
        };
      });
      setClientes(enrichedClientes);
    } catch (e) {
      console.error('Error loading clientes:', e);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadClientesData();
  }, [loadClientesData]);

  const filterConfig: Filter[] = [
    {
      key: 'buscar',
      label: 'Buscar (Nombre/NIF)',
      type: 'text',
      placeholder: 'Razón social, NIF...',
    },
    {
      key: 'activo',
      label: 'Estado',
      type: 'select',
      options: [
        { value: '', label: 'Todos' },
        { value: 'activo', label: 'Activo' },
        { value: 'inactivo', label: 'Inactivo' },
      ],
    },
    {
      key: 'estadoVisual',
      label: 'Estado Visual',
      type: 'select',
      options: [
        { value: '', label: 'Todos' },
        { value: 'green', label: 'Verde (OK)' },
        { value: 'amber', label: 'Ámbar (Atención)' },
        { value: 'red', label: 'Rojo (Urgente)' },
      ],
    },
  ];

  const clientesFiltrados = clientes.filter(c => {
    if (filtros.buscar) {
      const busqueda = filtros.buscar.toLowerCase();
      if (!c.razonSocial.toLowerCase().includes(busqueda) && 
          !c.nif.toLowerCase().includes(busqueda) &&
          !c.nombreComercial?.toLowerCase().includes(busqueda)) {
        return false;
      }
    }
    if (filtros.activo && c.estado !== filtros.activo) return false;
    if (filtros.estadoVisual && c.estadoVisual !== filtros.estadoVisual) return false;
    return true;
  });

  const handleFiltroChange = (key: string, value: any) => {
    setFiltros({ ...filtros, [key]: value });
  };

  const handleLimpiarFiltros = () => {
    setFiltros({});
  };

  const handleVerDetalle = (cliente: ClienteDetailed) => {
    setClienteSeleccionado(cliente);
    setMostrarDetalle(true);
    loadAIContext(cliente.id);
  };

  const loadAIContext = async (clienteId: string) => {
    setLoadingAI(true);
    try {
      const { data, error } = await supabase
        .from('ai_dashboard_insights')
        .select('*')
        .eq('scope', 'cliente')
        .eq('target_id', clienteId)
        .eq('activo', true)
        .order('generado_en', { ascending: false });

      if (error || !data || data.length === 0) {
        setAIContext(null);
        return;
      }

      const resumen = data.find(d => d.tipo === 'resumen');
      const alertas = data.filter(d => d.tipo === 'alerta');
      const recs = data.filter(d => d.tipo === 'recomendacion');
      const trends = data.filter(d => d.tipo === 'tendencia');

      setAIContext({
        resumenGeneral: resumen?.contenido || 'Sin datos de análisis disponibles.',
        alertasActivas: alertas.map(a => a.contenido),
        recomendaciones: recs.map(r => r.contenido),
        tendencias: trends.map(t => ({
          label: t.titulo || '',
          value: t.contenido || '',
          tendencia: (t.metadata as any)?.tendencia || 'stable',
        })),
      });
    } catch (err) {
      console.error('Error loading AI context:', err);
      setAIContext(null);
    } finally {
      setLoadingAI(false);
    }
  };

  const columns = [
    {
      key: 'razonSocial',
      header: 'Cliente',
      sortable: true,
      render: (cliente: ClienteDetailed) => (
        <div className="flex items-center space-x-3">
          <span className={cn(
            'w-2.5 h-2.5 rounded-full flex-shrink-0',
            cliente.estadoVisual === 'green' ? 'bg-emerald-400' :
            cliente.estadoVisual === 'amber' ? 'bg-amber-400' : 'bg-red-400'
          )} />
          <div>
            <div className="font-light text-calm-text">{cliente.razonSocial}</div>
            <div className="text-xs font-light text-calm-muted font-mono">{cliente.nif}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'docsPendientes',
      header: 'Docs Pendientes',
      sortable: true,
      render: (cliente: ClienteDetailed) => (
        <div className="text-sm">
          <div className={cn(
            'font-light',
            cliente.docsPendientes > 10 ? 'text-red-500' :
            cliente.docsPendientes > 5 ? 'text-amber-500' : 'text-emerald-600'
          )}>
            {cliente.docsPendientes}
          </div>
          <div className="text-xs font-light text-calm-muted">{cliente.docsIAProcesados} IA proc.</div>
        </div>
      ),
    },
    {
      key: 'incidenciasAbiertas',
      header: 'Incidencias',
      sortable: true,
      render: (cliente: ClienteDetailed) => (
        <Badge
          variant={
            cliente.incidenciasAbiertas > 2 ? 'destructive' :
            cliente.incidenciasAbiertas > 0 ? 'default' : 'secondary'
          }
          className={cn(
            'rounded-full font-light',
            cliente.incidenciasAbiertas > 2
              ? 'bg-red-50 text-red-600 border-red-200 hover:bg-red-50'
              : cliente.incidenciasAbiertas > 0
                ? 'bg-amber-50 text-amber-600 border-amber-200 hover:bg-amber-50'
                : 'bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-50'
          )}
        >
          {cliente.incidenciasAbiertas}
        </Badge>
      ),
    },
    {
      key: 'resultado',
      header: 'Resultado/Margen',
      render: (cliente: ClienteDetailed) => (
        <div className="text-sm">
          <div className={cn(
            'font-light',
            cliente.resultadoActual >= 0 ? 'text-emerald-600' : 'text-red-500'
          )}>
            {new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(cliente.resultadoActual)}
          </div>
          <div className="text-xs font-light text-calm-muted">{cliente.margenActual.toFixed(1)}% margen</div>
        </div>
      ),
    },
    {
      key: 'ultimaActividad',
      header: 'Última Actividad',
      sortable: true,
      render: (cliente: ClienteDetailed) => (
        <div className="text-xs font-light text-calm-muted">
          {new Date(cliente.ultimaActividad).toLocaleDateString('es-ES')}
        </div>
      ),
    },
    {
      key: 'estado',
      header: 'Estado',
      sortable: true,
      render: (cliente: ClienteDetailed) => (
        <Badge
          variant={cliente.estado === 'activo' ? 'default' : 'secondary'}
          className={cn(
            'rounded-full gap-1.5 font-light',
            cliente.estado === 'activo'
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50'
              : 'bg-calm-bg text-calm-muted border-calm-border hover:bg-calm-bg'
          )}
        >
          <span className={cn(
            'w-1.5 h-1.5 rounded-full',
            cliente.estado === 'activo' ? 'bg-emerald-500' : 'bg-calm-accent'
          )} />
          {cliente.estado === 'activo' ? 'Activo' : 'Inactivo'}
        </Badge>
      ),
    },
  ];

  const estadisticas = {
    total: clientes.length,
    activos: clientes.filter(c => c.estado === 'activo').length,
    verde: clientes.filter(c => c.estadoVisual === 'green').length,
    ambar: clientes.filter(c => c.estadoVisual === 'amber').length,
    rojo: clientes.filter(c => c.estadoVisual === 'red').length,
    totalDocumentos: clientes.reduce((sum, c) => sum + c.docsPendientes, 0),
  };

  /* ── Loading skeleton ── */
  if (loading) {
    return (
      <Layout rol="asesor">
        <div className="px-12 pb-12 pt-10 space-y-12">
          <div className="flex justify-between items-center">
            <div className="space-y-3">
              <Skeleton className="h-10 w-56 rounded-huge" />
              <Skeleton className="h-4 w-72 rounded-huge" />
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <Skeleton key={i} className="h-24 rounded-huge" />
            ))}
          </div>
          <Skeleton className="h-12 rounded-huge" />
          <Skeleton className="h-[400px] rounded-huge" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout rol="asesor">
      <div className="h-[calc(100vh-72px)] overflow-y-auto px-12 pb-12 pt-10 space-y-12">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-5xl font-display font-light text-calm-text">Clientes Asignados</h1>
            <p className="text-sm font-light text-calm-muted mt-2">Gestión y supervisión detallada de tus clientes</p>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-6">
          <Card className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge shadow-none hover:shadow-sm transition-shadow">
            <CardContent className="p-8">
              <div className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Total Clientes</div>
              <div className="text-metric font-display text-calm-text mt-2">{estadisticas.total}</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-white to-emerald-50/20 border border-emerald-200/40 rounded-huge shadow-none hover:shadow-sm transition-shadow">
            <CardContent className="p-8">
              <div className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Activos</div>
              <div className="text-metric font-display text-emerald-600 mt-2">{estadisticas.activos}</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-white to-emerald-50/20 border border-emerald-200/40 rounded-huge shadow-none hover:shadow-sm transition-shadow">
            <CardContent className="p-8">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-400" />
                <span className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Verde</span>
              </div>
              <div className="text-metric font-display text-calm-text mt-2">{estadisticas.verde}</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-white to-amber-50/20 border border-amber-200/40 rounded-huge shadow-none hover:shadow-sm transition-shadow">
            <CardContent className="p-8">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-amber-400" />
                <span className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Ámbar</span>
              </div>
              <div className="text-metric font-display text-calm-text mt-2">{estadisticas.ambar}</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-white to-red-50/20 border border-red-200/40 rounded-huge shadow-none hover:shadow-sm transition-shadow">
            <CardContent className="p-8">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-red-400" />
                <span className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Rojo</span>
              </div>
              <div className="text-metric font-display text-calm-text mt-2">{estadisticas.rojo}</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge shadow-none hover:shadow-sm transition-shadow">
            <CardContent className="p-8">
              <div className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Docs Pendientes</div>
              <div className="text-metric font-display text-amber-500 mt-2">{estadisticas.totalDocumentos}</div>
            </CardContent>
          </Card>
        </div>

        <FilterBar
          filters={filterConfig}
          values={filtros}
          onChange={handleFiltroChange}
          onClear={handleLimpiarFiltros}
        />

        <Card className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge shadow-none">
          <CardContent className="p-6">
            <DataTable
              data={clientesFiltrados}
              columns={columns}
              onRowClick={handleVerDetalle}
              actions={(cliente: ClienteDetailed) => (
                <div className="flex items-center space-x-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      router.push(`/asesor/documentos?cliente=${cliente.id}`);
                    }}
                    className="text-calm-muted hover:text-calm-text h-8 w-8 rounded-huge"
                    title="Ver documentos"
                  >
                    <span className="material-symbols-outlined text-[16px]">description</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      router.push(`/asesor/incidencias?cliente=${cliente.id}`);
                    }}
                    className="text-calm-muted hover:text-amber-500 h-8 w-8 rounded-huge"
                    title="Ver incidencias"
                  >
                    <span className="material-symbols-outlined text-[16px]">warning</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      router.push(`/asesor/panel-financiero?cliente=${cliente.id}`);
                    }}
                    className="text-calm-muted hover:text-emerald-500 h-8 w-8 rounded-huge"
                    title="Panel financiero"
                  >
                    <span className="material-symbols-outlined text-[16px]">payments</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleVerDetalle(cliente);
                    }}
                    className="text-calm-muted hover:text-calm-text h-8 w-8 rounded-huge"
                    title="Ver detalle completo"
                  >
                    <span className="material-symbols-outlined text-[16px]">visibility</span>
                  </Button>
                </div>
              )}
            />
          </CardContent>
        </Card>

        {/* Detail Dialog with AI Context Sidebar */}
        <Dialog
          open={mostrarDetalle && !!clienteSeleccionado}
          onOpenChange={(open) => {
            if (!open) {
              setMostrarDetalle(false);
              setClienteSeleccionado(null);
              setShowAIContext(false);
            }
          }}
        >
          {clienteSeleccionado && (
            <DialogContent className="max-w-7xl h-[90vh] p-0 rounded-huge border-calm-border">
              <div className="flex h-full">
                {/* Main Detail Panel */}
                <div className="flex-1 overflow-y-auto p-8">
                  <DialogHeader className="mb-8">
                    <div className="flex items-center justify-between pr-8">
                      <div>
                        <div className="flex items-center space-x-3">
                          <span className={cn(
                            'w-3 h-3 rounded-full',
                            clienteSeleccionado.estadoVisual === 'green' ? 'bg-emerald-400' :
                            clienteSeleccionado.estadoVisual === 'amber' ? 'bg-amber-400' : 'bg-red-400'
                          )} />
                          <DialogTitle className="text-2xl font-display font-light text-calm-text">
                            {clienteSeleccionado.razonSocial}
                          </DialogTitle>
                        </div>
                        <p className="text-sm font-light text-calm-muted ml-6 mt-1 font-mono">{clienteSeleccionado.nif}</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowAIContext(!showAIContext)}
                        className="gap-2 border border-calm-border rounded-huge px-4 py-2 text-sm font-light text-calm-muted hover:bg-white transition-colors"
                      >
                        <span className="material-symbols-outlined text-[16px]">smart_toy</span>
                        {showAIContext ? 'Ocultar' : 'Mostrar'} Contexto IA
                      </Button>
                    </div>
                  </DialogHeader>

                  <div className="space-y-8">
                    {/* Fiscal Data & Advisors */}
                    <div className="grid grid-cols-2 gap-6">
                      <Card className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge shadow-none">
                        <CardHeader className="px-8 pt-8 pb-3">
                          <CardTitle className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em] flex items-center gap-2">
                            <span className="material-symbols-outlined text-[14px]">badge</span>
                            Datos Fiscales
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="px-8 pb-8">
                          <div className="space-y-3 text-sm">
                            <div className="flex justify-between">
                              <span className="font-light text-calm-muted">NIF:</span>
                              <span className="font-light text-calm-text font-mono">{clienteSeleccionado.nif}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="font-light text-calm-muted">Razón Social:</span>
                              <span className="font-light text-calm-text">{clienteSeleccionado.razonSocial}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="font-light text-calm-muted">Dirección Fiscal:</span>
                              <span className="font-light text-calm-text text-right">{clienteSeleccionado.direccionFiscal}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="font-light text-calm-muted">Email:</span>
                              <span className="font-light text-calm-text">{clienteSeleccionado.email}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="font-light text-calm-muted">Teléfono:</span>
                              <span className="font-light text-calm-text">{clienteSeleccionado.telefono}</span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge shadow-none">
                        <CardHeader className="px-8 pt-8 pb-3">
                          <CardTitle className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em] flex items-center gap-2">
                            <span className="material-symbols-outlined text-[14px]">person</span>
                            Asesores
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="px-8 pb-8">
                          <div className="space-y-2">
                            {clienteSeleccionado.asesoresAsignados.map((asesorId, idx) => (
                              <div key={asesorId} className="flex items-center space-x-3 p-3 bg-calm-bg rounded-2xl">
                                <span className="material-symbols-outlined text-[20px] text-calm-accent">account_circle</span>
                                <div>
                                  <div className="text-sm font-light text-calm-text">Asesor {idx + 1}</div>
                                  <div className="text-xs font-light text-calm-muted font-mono">ID: {asesorId.substring(0, 8)}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Doc & AI Status */}
                    <div className="grid grid-cols-4 gap-6">
                      <Card className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge shadow-none">
                        <CardContent className="p-6">
                          <div className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Docs Pendientes</div>
                          <div className="text-metric font-display text-calm-text mt-2">{clienteSeleccionado.docsPendientes}</div>
                        </CardContent>
                      </Card>
                      <Card className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge shadow-none">
                        <CardContent className="p-6">
                          <div className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">IA Procesados</div>
                          <div className="text-metric font-display text-calm-text mt-2">{clienteSeleccionado.docsIAProcesados}</div>
                        </CardContent>
                      </Card>
                      <Card className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge shadow-none">
                        <CardContent className="p-6">
                          <div className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Incidencias</div>
                          <div className="text-metric font-display text-amber-500 mt-2">{clienteSeleccionado.incidenciasAbiertas}</div>
                        </CardContent>
                      </Card>
                      <Card className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge shadow-none">
                        <CardContent className="p-6">
                          <div className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Último Asiento ERP</div>
                          <div className="text-lg font-display font-light text-calm-text mt-2">{clienteSeleccionado.asientoERPUltimo || 'N/A'}</div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Mini Finance */}
                    <Card className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge shadow-none">
                      <CardHeader className="px-8 pt-8 pb-3">
                        <CardTitle className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em] flex items-center gap-2">
                          <span className="material-symbols-outlined text-[14px]">account_balance</span>
                          Mini Finanzas
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="px-8 pb-8">
                        <div className="grid grid-cols-3 gap-6">
                          <div className="bg-calm-bg p-6 rounded-2xl">
                            <div className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Resultado</div>
                            <div className={cn(
                              'text-2xl font-display font-light mt-2',
                              clienteSeleccionado.resultadoActual >= 0 ? 'text-emerald-600' : 'text-red-500'
                            )}>
                              {new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(clienteSeleccionado.resultadoActual)}
                            </div>
                          </div>
                          <div className="bg-calm-bg p-6 rounded-2xl">
                            <div className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Margen</div>
                            <div className="text-2xl font-display font-light text-calm-text mt-2">{clienteSeleccionado.margenActual.toFixed(1)}%</div>
                          </div>
                          <div className="bg-calm-bg p-6 rounded-2xl">
                            <div className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em]">Estado</div>
                            <div className="flex items-center gap-2 mt-2">
                              <span className={cn(
                                'w-3 h-3 rounded-full',
                                clienteSeleccionado.estadoVisual === 'green' ? 'bg-emerald-400' :
                                clienteSeleccionado.estadoVisual === 'amber' ? 'bg-amber-400' : 'bg-red-400'
                              )} />
                              <span className="text-2xl font-display font-light text-calm-text">
                                {clienteSeleccionado.estadoVisual === 'green' ? 'OK' : 
                                 clienteSeleccionado.estadoVisual === 'amber' ? 'Atención' : 'Urgente'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Quick Actions */}
                    <Card className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge shadow-none">
                      <CardHeader className="px-8 pt-8 pb-3">
                        <CardTitle className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em] flex items-center gap-2">
                          <span className="material-symbols-outlined text-[14px]">bolt</span>
                          Acciones Rápidas
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="px-8 pb-8">
                        <div className="grid grid-cols-4 gap-4">
                          <Button
                            onClick={() => router.push(`/asesor/documentos?cliente=${clienteSeleccionado.id}`)}
                            className="gap-2 bg-calm-text text-white rounded-huge px-5 py-2.5 text-sm font-light hover:bg-calm-text/90 transition-colors"
                          >
                            <span className="material-symbols-outlined text-[16px]">description</span>
                            Documentos
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => router.push(`/asesor/incidencias?cliente=${clienteSeleccionado.id}`)}
                            className="gap-2 border border-calm-border rounded-huge px-5 py-2.5 text-sm font-light text-calm-muted hover:bg-white transition-colors"
                          >
                            <span className="material-symbols-outlined text-[16px]">warning</span>
                            Incidencias
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => router.push(`/asesor/panel-financiero?cliente=${clienteSeleccionado.id}`)}
                            className="gap-2 border border-calm-border rounded-huge px-5 py-2.5 text-sm font-light text-calm-muted hover:bg-white transition-colors"
                          >
                            <span className="material-symbols-outlined text-[16px]">payments</span>
                            Finanzas
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => router.push(`/asesor/asistente?cliente=${clienteSeleccionado.id}`)}
                            className="gap-2 border border-calm-border rounded-huge px-5 py-2.5 text-sm font-light text-calm-muted hover:bg-white transition-colors"
                          >
                            <span className="material-symbols-outlined text-[16px]">smart_toy</span>
                            Asistente
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* AI Context Sidebar */}
                {showAIContext && (
                  <div className="w-96 bg-calm-bg border-l border-calm-border overflow-y-auto p-8">
                    <h3 className="text-lg font-display font-light text-calm-text mb-6 flex items-center gap-2">
                      <span className="material-symbols-outlined text-[20px] text-calm-accent">smart_toy</span>
                      Contexto IA
                    </h3>

                    {loadingAI ? (
                      <div className="flex flex-col items-center justify-center py-12">
                        <div className="w-8 h-8 border-2 border-fresh-sky-400 border-t-transparent rounded-full animate-spin" />
                        <p className="mt-3 text-xs text-calm-muted">Cargando análisis IA…</p>
                      </div>
                    ) : !aiContext ? (
                      <div className="flex flex-col items-center justify-center py-12 text-center">
                        <span className="material-symbols-outlined text-3xl text-calm-muted mb-2">smart_toy</span>
                        <p className="text-sm text-calm-muted font-light">Sin datos de IA disponibles.</p>
                        <p className="text-xs text-calm-muted mt-1">Los insights se generan 2 veces al día.</p>
                      </div>
                    ) : (
                      <>
                    {/* General Summary */}
                    <div className="mb-8">
                      <h4 className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em] mb-3">Resumen General</h4>
                      <Card className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-2xl shadow-none">
                        <CardContent className="p-4">
                          <p className="text-sm font-light text-calm-muted leading-relaxed">
                            {aiContext.resumenGeneral}
                          </p>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Active Alerts */}
                    <div className="mb-8">
                      <h4 className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em] mb-3 flex items-center gap-1.5">
                        <span className="material-symbols-outlined text-[12px]">warning</span>
                        Alertas Activas
                      </h4>
                      <div className="space-y-2">
                        {aiContext.alertasActivas.map((alerta, idx) => (
                          <div key={idx} className="bg-amber-50/50 border-l-2 border-amber-300 p-3 rounded-r-2xl">
                            <p className="text-xs font-light text-calm-text">{alerta}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Recommendations */}
                    <div className="mb-8">
                      <h4 className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em] mb-3 flex items-center gap-1.5">
                        <span className="material-symbols-outlined text-[12px]">lightbulb</span>
                        Recomendaciones
                      </h4>
                      <div className="space-y-2">
                        {aiContext.recomendaciones.map((rec, idx) => (
                          <div key={idx} className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 p-3 rounded-2xl">
                            <p className="text-xs font-light text-calm-text">{rec}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Trends */}
                    <div>
                      <h4 className="text-[10px] font-medium text-calm-muted uppercase tracking-[0.15em] mb-3 flex items-center gap-1.5">
                        <span className="material-symbols-outlined text-[12px]">trending_up</span>
                        Tendencias
                      </h4>
                      <div className="space-y-2">
                        {aiContext.tendencias.map((trend, idx) => (
                          <Card key={idx} className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-2xl shadow-none">
                            <CardContent className="py-3 px-4 flex items-center justify-between">
                              <span className="text-xs font-light text-calm-muted">{trend.label}</span>
                              <div className="flex items-center space-x-2">
                                <span className="text-sm font-display font-light text-calm-text">{trend.value}</span>
                                <span className="material-symbols-outlined text-[16px] text-calm-accent">
                                  {trend.tendencia === 'up' ? 'trending_up' : trend.tendencia === 'down' ? 'trending_down' : 'trending_flat'}
                                </span>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </DialogContent>
          )}
        </Dialog>
      </div>
    </Layout>
  );
}
